import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Fade,
  useTheme,
  SelectChangeEvent,
  Grid,
  Avatar,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Divider,
  useMediaQuery,
  Zoom,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Sort as SortIcon,
  Search as SearchIcon,
  Build as BuildIcon,
  Engineering as BodyworkIcon,
  Brush as CleaningIcon,
  Palette as UpholsteryIcon,
  LocalGasStation as FuelIcon,
  Security as InsuranceIcon,
  Receipt as TaxIcon,
  Settings as AccessoriesIcon,
  QuestionMark as OtherIcon,
  Construction as MaintenanceIcon,
  AttachMoney as MoneyIcon,
  DirectionsCar as CarIcon,
  DateRange as DateIcon,
  ArrowBack as BackIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
} from '@mui/icons-material';
import { animated } from '@react-spring/web';
import { Expense, ExpenseCategory, EXPENSE_CATEGORIES, EXPENSE_CATEGORY_COLORS, Vehicle } from '../../types';        
import expenseService from '../../services/expenseService';
import vehicleService from '../../services/vehicleService';
// @ts-ignore
import { format, parseISO } from 'date-fns';
// @ts-ignore
import ptBR from 'date-fns/locale/pt-BR';
import { pdf } from '@react-pdf/renderer';
import ExpensesReportPDF from './ExpensesReportPDF';
import { exportExpensesReportExcel } from '../../utils/reportExcelExports';

const AnimatedTableRow = animated(TableRow);
const AnimatedCard = animated(Card);

// Componente para exibir o ícone da categoria de despesa
const CategoryIcon = ({ category }: { category: ExpenseCategory }) => {
  switch (category) {
    case 'maintenance':
      return <MaintenanceIcon />;
    case 'mechanics':
      return <BuildIcon />;
    case 'bodywork':
      return <BodyworkIcon />;
    case 'upholstery':
      return <UpholsteryIcon />;
    case 'fuel':
      return <FuelIcon />;
    case 'insurance':
      return <InsuranceIcon />;
    case 'tax':
      return <TaxIcon />;
    case 'accessories':
      return <AccessoriesIcon />;
    case 'cleaning':
      return <CleaningIcon />;
    case 'other':
      return <OtherIcon />;
    default:
      return <MaintenanceIcon />;
  }
};

const ExpenseList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | 'all'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'last30' | 'last90' | 'thisYear' | 'custom'>('all');
  const [periodStart, setPeriodStart] = useState<string>(''); // YYYY-MM-DD
  const [periodEnd, setPeriodEnd] = useState<string>(''); // YYYY-MM-DD
  const [vehicleFilter, setVehicleFilter] = useState<number | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'category'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'price_high' | 'price_low'>('newest');
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<null | HTMLElement>(null);
  // const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // Para armazenar a mensagem de sucesso
  const [openSnackbar, setOpenSnackbar] = useState(false); // Controla a visibilidade do Snackbar
  /** Veículo selecionado: null = lista por veículo, number = despesas daquele veículo */
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [vehiclesById, setVehiclesById] = useState<Map<number, Vehicle>>(new Map());

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        setLoading(true);
        
        // Busca despesas e veículos para garantir imagens no card
        const [expensesData, vehiclesData] = await Promise.all([
          expenseService.getAllExpenses(),
          vehicleService.getAllVehicles(),
        ]);
        setExpenses(expensesData);
        setVehiclesById(new Map((vehiclesData || []).map((v) => [v.id, v])));
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar despesas:', error);
        setError('Não foi possível carregar as despesas. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchExpenses();
  }, []);

  useEffect(() => {
    // Automatically switch to card view on mobile devices
    if (isMobile) {
      setViewMode('card');
    } else {
      setViewMode('table');
    }
  }, [isMobile]);

  const handleAddExpense = () => {
    navigate('/despesas/novo');
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');
      const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
      const logoUrl = `${base}/assets/logos/${encodeURIComponent('MARCA - R MOTORS horizontal.png')}`;

      const listForPdf = filteredExpenses;
      const doc = (
        <ExpensesReportPDF
          expenses={listForPdf}
          generatedAtLabel={generatedAtLabel}
          logoUrl={logoUrl}
          title={selectedVehicleId != null ? 'Despesas do veículo (lista)' : 'Despesas (lista)'}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Despesas-${generatedAtLabel.replace(/[/:\\s]/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError('Não foi possível gerar o PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };


  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');
      await exportExpensesReportExcel({
        expenses: filteredExpenses,
        generatedAtLabel,
        title: selectedVehicleId != null ? 'Despesas do veículo (lista)' : 'Despesas (lista)',
      });
    } catch (e) {
      console.error(e);
      setError('Não foi possível gerar o Excel.');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleEditExpense = (id: number) => {
    navigate(`/despesas/editar/${id}`);
  };

  const handleDeleteExpense = async (id: number) => {
    try {
      // Aguarde a resolução da Promise

      const confirm = window.confirm('Tem certeza que deseja excluir a despesa?')

      if(confirm){
        const data = await expenseService.deleteExpense(id);

        setExpenses(expenses.filter(item => item.id !== id));
  
        if (data === 204) {
          setSuccessMessage('Despesa excluída com sucesso!');
          setOpenSnackbar(true); // Abre o Snackbar
  
          setTimeout(() => {
            setOpenSnackbar(false); 
            setSuccessMessage(null); 
          }, 3000);
        }
      }

     
    } catch (error) {
      alert('Erro ao excluir a despesa')
      console.error('Erro ao excluir a despesa:', error);
    }
  };

  const handleViewExpense = (id: number) => {
    navigate(`/despesas/${id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, expenseId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedExpenseId(expenseId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedExpenseId(null);
  };

  const handleCategoryFilterChange = (event: SelectChangeEvent<string>) => {
    setCategoryFilter(event.target.value as ExpenseCategory | 'all');
  };

  const handleDateFilterChange = (event: SelectChangeEvent<string>) => {
    const v = event.target.value as typeof dateFilter;
    setDateFilter(v);
    // Ao trocar para presets, limpa intervalo manual para evitar confusão visual
    if (v !== 'custom') {
      setPeriodStart('');
      setPeriodEnd('');
    }
  };

  const handleVehicleFilterChange = (event: SelectChangeEvent<string>) => {
    setVehicleFilter(event.target.value as number | 'all');
  };

  const handleSortByChange = (sortField: 'date' | 'amount' | 'category') => {
    if (sortBy === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sortField);
      setSortDirection('desc');
    }
  };

  // Filtrar despesas com base nos critérios de pesquisa
  const filteredExpenses = expenses.filter((expense) => {
    // Filtro de texto
    const matchesSearch = (
      expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vehicle?.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vehicle?.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(expense.vehicle?.plate || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filtro de categoria
    const matchesCategory = categoryFilter === 'all' || expense.category === categoryFilter;

    // Filtro de veículo
    const matchesVehicle = vehicleFilter === 'all' || expense.vehicle_id === vehicleFilter;

    // Filtro de data
    let matchesDate = true;
    const today = new Date();
    const expenseDate = new Date(expense.expense_date);
    
    if (dateFilter === 'last30') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      matchesDate = expenseDate >= thirtyDaysAgo;
    } else if (dateFilter === 'last90') {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(today.getDate() - 90);
      matchesDate = expenseDate >= ninetyDaysAgo;
    } else if (dateFilter === 'thisYear') {
      matchesDate = expenseDate.getFullYear() === today.getFullYear();
    } else if (dateFilter === 'custom') {
      // Período manual: considera inclusivo (>= início e <= fim)
      const start = periodStart ? new Date(`${periodStart}T00:00:00`) : null;
      const end = periodEnd ? new Date(`${periodEnd}T23:59:59`) : null;
      if (start && !Number.isNaN(start.getTime())) matchesDate = matchesDate && expenseDate >= start;
      if (end && !Number.isNaN(end.getTime())) matchesDate = matchesDate && expenseDate <= end;
    }

    return matchesSearch && matchesCategory && matchesVehicle && matchesDate;
  });

  // Ordenar despesas
  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    if (sortBy === 'date') {
      const dateA = new Date(a.expense_date).getTime();
      const dateB = new Date(b.expense_date).getTime();
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    } else if (sortBy === 'amount') {
      return sortDirection === 'asc' ? a.amount - b.amount : b.amount - a.amount;
    } else if (sortBy === 'category') {
      return sortDirection === 'asc'
        ? a.category.localeCompare(b.category)
        : b.category.localeCompare(a.category);
    }
    return 0;
  });

  // Calcular total de despesas filtradas
  const totalExpenses = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  const getVehiclePrimaryImageUrl = useMemo(() => {
    return (vehicleId: number): string | null => {
      const v = vehiclesById.get(vehicleId);
      return v?.images?.[0]?.url ?? null;
    };
  }, [vehiclesById]);

  // Agrupar despesas por veículo (para a lista "por carro")
  const expensesByVehicle = React.useMemo(() => {
    const map = new Map<number, { vehicleName: string; vehicleId: number; expenses: Expense[] }>();
    filteredExpenses.forEach((expense) => {
      const id = expense.vehicle_id;
      const vehicle = vehiclesById.get(id) ?? expense.vehicle;
      const name = vehicle
        ? `${vehicle.brand} ${vehicle.model} (${vehicle.plate})`
        : `Veículo #${id}`;
      if (!map.has(id)) map.set(id, { vehicleName: name, vehicleId: id, expenses: [] });
      map.get(id)!.expenses.push(expense);
    });
    return Array.from(map.entries()).map(([vehicleId, { vehicleName, expenses }]) => ({
      vehicleId,
      vehicleName,
      imageUrl: getVehiclePrimaryImageUrl(vehicleId),
      expenses,
      total: expenses.reduce((s, e) => s + e.amount, 0),
      count: expenses.length,
    }));
  }, [filteredExpenses, getVehiclePrimaryImageUrl, vehiclesById]);

  // Despesas a exibir: quando há veículo selecionado, só desse veículo; senão usa lista por veículo
  const displayExpenses = selectedVehicleId != null
    ? filteredExpenses.filter((e) => e.vehicle_id === selectedVehicleId)
    : [];

  // Agrupar despesas por categoria para estatísticas
  const expensesByCategory = filteredExpenses.reduce((acc, expense) => {
    acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
    return acc;
  }, {} as Record<ExpenseCategory, number>);

  // Estatísticas da visão atual (global ou do veículo selecionado)
  const totalDisplay = selectedVehicleId != null
    ? displayExpenses.reduce((s, e) => s + e.amount, 0)
    : totalExpenses;
  const countDisplay = selectedVehicleId != null ? displayExpenses.length : filteredExpenses.length;
  const expensesByCategoryDisplay = selectedVehicleId != null
    ? displayExpenses.reduce((acc, expense) => {
        acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
        return acc;
      }, {} as Record<ExpenseCategory, number>)
    : expensesByCategory;

  // Formatação de datas e valores
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'; 
    
    try {
      const cleanDateString = dateString.split('T')[0];  // Vai pegar apenas '2025-05-01'
      const [year, month, day] = cleanDateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-'; 
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Animação simples para linhas da tabela
  const rowAnimationStyle = {
    opacity: 1,
    transform: 'translateY(0)'
  };

  // Estilos de animação para cards
  const cardAnimationStyle = {
    opacity: 1,
    transform: 'scale(1)'
  };

  // Calcular despesas paginadas (na visão por veículo usa só as despesas do veículo selecionado)
  const expensesToPaginate = selectedVehicleId != null ? displayExpenses : filteredExpenses;
  const paginatedExpenses = expensesToPaginate.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Calcular número total de páginas
  const totalPages = Math.ceil(expensesToPaginate.length / rowsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSelectVehicle = (vehicleId: number) => {
    setSelectedVehicleId(vehicleId);
    setPage(1);
  };

  const handleBackToVehicles = () => {
    setSelectedVehicleId(null);
    setPage(1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  // Array de veículos únicos para o filtro
  const uniqueVehicles = Array.from(
    new Set(expenses.map(expense => expense.vehicle_id))
  ).map(vehicleId => {
    const vehicle = expenses.find(expense => expense.vehicle_id === vehicleId)?.vehicle;
    return vehicle ? {
      id: vehicle.id,
      name: `${vehicle.brand} ${vehicle.model} (${vehicle.plate})`
    } : null;
  }).filter(Boolean) as { id: number; name: string }[];

  return (
    <Fade in={true} timeout={800}>
      <div>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000} 
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: 'top',   
          horizontal: 'center'
        }}
        sx={{
          marginTop: '55px', 
          zIndex: 9,
        }}
    
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
        <Box 

          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {selectedVehicleId != null && (
              <Button
                startIcon={<BackIcon />}
                onClick={handleBackToVehicles}
                sx={{ borderRadius: '8px' }}
              >
                Voltar aos veículos
              </Button>
            )}
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {selectedVehicleId != null
                  ? (expensesByVehicle.find((v) => v.vehicleId === selectedVehicleId)?.vehicleName ?? 'Despesas do veículo')
                  : 'Despesas'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {selectedVehicleId != null
                  ? 'Despesas cadastradas para este veículo'
                  : 'Clique em um veículo para ver suas despesas'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Button
              variant="outlined"
              color="success"
              startIcon={downloadingExcel ? <CircularProgress size={18} color="inherit" /> : <ExcelIcon />}
              onClick={() => void handleDownloadExcel()}
              disabled={downloadingExcel || filteredExpenses.length === 0}
            >
              {downloadingExcel ? 'Gerando Excel...' : 'Baixar Excel'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<PdfIcon />}
              onClick={() => void handleDownloadPdf()}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddExpense}
              sx={{
                py: 1.2,
                px: 3,
                borderRadius: '8px',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                },
              }}
            >
              Nova Despesa
            </Button>
          </Box>
        </Box>

        {/* Área de estatísticas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                  <MoneyIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Total de Despesas
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {formatCurrency(totalDisplay)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {countDisplay} registros
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: EXPENSE_CATEGORY_COLORS.mechanics }}>
                  <BuildIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Mecânica
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {formatCurrency(expensesByCategoryDisplay.mechanics || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {(selectedVehicleId != null ? displayExpenses : filteredExpenses).filter(e => e.category === 'mechanics').length} registros
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: EXPENSE_CATEGORY_COLORS.fuel }}>
                  <FuelIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Combustível
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {formatCurrency(expensesByCategoryDisplay.fuel || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {(selectedVehicleId != null ? displayExpenses : filteredExpenses).filter(e => e.category === 'fuel').length} registros
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: EXPENSE_CATEGORY_COLORS.bodywork }}>
                  <BodyworkIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Funilaria
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {formatCurrency(expensesByCategoryDisplay.bodywork || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {(selectedVehicleId != null ? displayExpenses : filteredExpenses).filter(e => e.category === 'bodywork').length} registros
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filtros e pesquisa */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 2, 
            mb: 4, 
            borderRadius: '12px',
            backdropFilter: 'blur(4px)',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por descrição, veículo ou placa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: 'rgba(0, 0, 0, 0.2)',
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                }
              }}>
                <InputLabel id="category-filter-label">Categoria</InputLabel>
                <Select
                  labelId="category-filter-label"
                  id="category-filter"
                  value={categoryFilter}
                  label="Categoria"
                  onChange={handleCategoryFilterChange}
                >
                  <MenuItem value="all">Todas Categorias</MenuItem>
                  {Object.entries(EXPENSE_CATEGORIES).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            bgcolor: EXPENSE_CATEGORY_COLORS[value as ExpenseCategory] 
                          }}
                        >
                          <CategoryIcon category={value as ExpenseCategory} />
                        </Avatar>
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                }
              }}>
                <InputLabel id="vehicle-filter-label">Veículo</InputLabel>
                <Select
                  labelId="vehicle-filter-label"
                  id="vehicle-filter"
                  value={vehicleFilter === 'all' ? 'all' : vehicleFilter.toString()}
                  label="Veículo"
                  onChange={handleVehicleFilterChange}
                >
                  <MenuItem value="all">Todos Veículos</MenuItem>
                  {uniqueVehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                }
              }}>
                <InputLabel id="date-filter-label">Período</InputLabel>
                <Select
                  labelId="date-filter-label"
                  id="date-filter"
                  value={dateFilter}
                  label="Período"
                  onChange={handleDateFilterChange}
                >
                  <MenuItem value="all">Todas datas</MenuItem>
                  <MenuItem value="last30">Últimos 30 dias</MenuItem>
                  <MenuItem value="last90">Últimos 90 dias</MenuItem>
                  <MenuItem value="thisYear">Este ano</MenuItem>
                  <MenuItem value="custom">Personalizado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {dateFilter === 'custom' && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="De"
                    type="date"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'rgba(0, 0, 0, 0.2)',
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Até"
                    type="date"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'rgba(0, 0, 0, 0.2)',
                      }
                    }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  startIcon={<SortIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const nextSort = sortBy === 'date' ? 'amount' : sortBy === 'amount' ? 'category' : 'date';
                    handleSortByChange(nextSort);
                  }}
                  sx={{ borderRadius: '8px' }}
                >
                  {sortBy === 'date' ? 'Por Data' : sortBy === 'amount' ? 'Por Valor' : 'Por Categoria'}
                  {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}

        {selectedVehicleId == null && expensesByVehicle.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: '12px' }}>
            <MoneyIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
            <Typography variant="h6">Nenhuma despesa encontrada</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '500px', mx: 'auto' }}>
              Não foram encontradas despesas com os filtros atuais. Tente modificar sua busca ou adicione uma nova despesa.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddExpense}
              sx={{ 
                py: 1.2, 
                borderRadius: '8px',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
              }}
            >
              Nova Despesa
            </Button>
          </Paper>
        ) : selectedVehicleId != null && displayExpenses.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: '12px' }}>
            <CarIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
            <Typography variant="h6">Nenhuma despesa para este veículo</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '500px', mx: 'auto' }}>
              Não há despesas cadastradas para o veículo selecionado com os filtros atuais.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBackToVehicles}
              sx={{ borderRadius: '8px' }}
            >
              Voltar aos veículos
            </Button>
          </Paper>
        ) : selectedVehicleId == null ? (
          /* Lista de veículos (cada um aparece uma vez; ao clicar, abre as despesas do veículo) */
          <Grid container spacing={3}>
            {expensesByVehicle.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={item.vehicleId}>
                <Zoom in={true} style={{ transitionDelay: `${index * 80}ms` }}>
                  <Card
                    sx={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      minHeight: 260,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                    onClick={() => handleSelectVehicle(item.vehicleId)}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        height: 180,
                        width: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      {item.imageUrl ? (
                        <>
                          <Box
                            component="img"
                            src={item.imageUrl}
                            alt={item.vehicleName}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '50%',
                              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                            }}
                          />
                        </>
                      ) : (
                        <Box
                          sx={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                            position: 'relative',
                          }}
                        >
                          <CarIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                        </Box>
                      )}
                    </Box>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {item.vehicleName}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                          size="small"
                          label={`${item.count} ${item.count === 1 ? 'despesa' : 'despesas'}`}
                          sx={{ borderRadius: '8px' }}
                        />
                        <Typography variant="body1" fontWeight="bold" color="error.main">
                          {formatCurrency(item.total)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        ) : viewMode === 'table' ? (
          <>
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: '12px', 
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                mb: 3
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(0, 0, 0, 0.2)'
                        : 'rgba(0, 0, 0, 0.05)',}}>
                    <TableCell>Data</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Veículo</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedExpenses.map((expense, index) => (
                    <AnimatedTableRow 
                      key={expense.id} 
                      hover
                      style={{
                        ...rowAnimationStyle,
                        transition: `all 0.3s ease ${index * 0.1}s`
                      }}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'rgba(63, 136, 197, 0.04)',
                          cursor: 'pointer',
                        }
                      }}
                      onClick={() => handleViewExpense(expense.id)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DateIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          {formatDate(expense.expense_date)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<CategoryIcon category={expense.category} />}
                          label={EXPENSE_CATEGORIES[expense.category]}
                          sx={{ 
                            bgcolor: `${EXPENSE_CATEGORY_COLORS[expense.category]}20`,
                            color: EXPENSE_CATEGORY_COLORS[expense.category],
                            fontWeight: 'medium',
                            '& .MuiChip-icon': {
                              color: EXPENSE_CATEGORY_COLORS[expense.category],
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          <Typography>
                            {expense.vehicle ? 
                              `${expense.vehicle.brand} ${expense.vehicle.model} (${expense.vehicle.plate})` : 
                              'Veículo não encontrado'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 250 }}>
                        <Typography noWrap title={expense.description}>
                          {expense.description}
                        </Typography>
                        {expense.notes && (
                          <Typography variant="body2" color="text.secondary" noWrap title={expense.notes}>
                            {expense.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="Mais opções" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, expense.id)}
                              sx={{ 
                                transition: 'all 0.2s',
                                '&:hover': { transform: 'rotate(90deg)' }
                              }}
                            >
                              <MoreIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </AnimatedTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <Grid container spacing={3}>
              {paginatedExpenses.map((expense, index) => (
                <Grid item xs={12} sm={6} key={expense.id}>
                  <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                    <Box>
                      <AnimatedCard 
                        style={{
                          ...cardAnimationStyle,
                          transition: `all 0.3s ease ${index * 0.1}s`
                        }}
                        sx={{ 
                          borderRadius: '16px',
                          overflow: 'hidden',
                          transition: 'all 0.3s',
                          cursor: 'pointer',
                          position: 'relative',
                          '&:hover': { 
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 20px rgba(0, 0, 0, 0.3)'
                          }
                        }}
                        onClick={() => handleViewExpense(expense.id)}
                      >
                        <Box 
                          sx={{ 
                            height: '90px', 
                            background: theme.palette.mode === 'dark' 
                              ? `linear-gradient(135deg, ${EXPENSE_CATEGORY_COLORS[expense.category]}50 0%, #121212 100%)`
                              : `linear-gradient(135deg, ${EXPENSE_CATEGORY_COLORS[expense.category]}50 0%, ${EXPENSE_CATEGORY_COLORS[expense.category]}20 100%)`,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            p: 2,
                          }}
                        >
                          <Avatar sx={{ bgcolor: EXPENSE_CATEGORY_COLORS[expense.category], width: 50, height: 50 }}>
                            <CategoryIcon category={expense.category} />
                          </Avatar>
                          <Chip
                            label={formatCurrency(expense.amount)}
                            size="small"
                            sx={{ 
                              position: 'absolute', 
                              top: 12, 
                              right: 12,
                              fontWeight: 'bold',
                              bgcolor: theme.palette.error.main + '20',
                              color: theme.palette.error.main,
                              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
                            }}
                          />
                        </Box>
                        
                        <CardContent>
                          <Box sx={{ mb: 1 }}>
                            <Chip
                              label={EXPENSE_CATEGORIES[expense.category]}
                              size="small"
                              sx={{ 
                                bgcolor: `${EXPENSE_CATEGORY_COLORS[expense.category]}20`,
                                color: EXPENSE_CATEGORY_COLORS[expense.category],
                                fontWeight: 'medium'
                              }}
                            />
                          </Box>
                          
                          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {expense.description}
                          </Typography>
                          
                          <Grid container spacing={2} sx={{ mb: 1 }}>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <DateIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Data: <Box component="span" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                                    {formatDate(expense.expense_date).split(' de ')[0]}
                                  </Box>
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CarIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  Veículo: <Box component="span" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                                    {expense.vehicle ? 
                                      `${expense.vehicle.model}` : 
                                      '-'}
                                  </Box>
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                          
                          {expense.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {expense.notes.length > 60 ? `${expense.notes.substring(0, 60)}...` : expense.notes}
                            </Typography>
                          )}
                          
                          <Divider sx={{ my: 1.5 }} />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Valor:
                            </Typography>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.error.main
                              }}
                            >
                              {formatCurrency(expense.amount)}
                            </Typography>
                          </Box>
                        </CardContent>
                        
                        <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                          <Box onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, expense.id)}
                            >
                              <MoreIcon />
                            </IconButton>
                          </Box>
                        </CardActions>
                      </AnimatedCard>
                    </Box>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </>
        )}

        {/* Menu de ações */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
            }
          }}
        >
          <MenuItem onClick={() => {
            if (selectedExpenseId) handleViewExpense(selectedExpenseId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver Detalhes</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedExpenseId) handleEditExpense(selectedExpenseId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedExpenseId) handleDeleteExpense(selectedExpenseId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.error.main }}>Excluir</ListItemText>
          </MenuItem>
        </Menu>
      </div>
    </Fade>
  );
};

export default ExpenseList; 